import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

export interface ConfirmationButton {
  label: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'danger';
  outlined?: boolean;
  action: string;
  loading?: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    TranslateModule,
    AppButtonComponent,
  ],
  templateUrl: './confirmation-dialog.component.html',
  styleUrl: './confirmation-dialog.component.css',
})
export class ConfirmationDialogComponent {
  @Input() visible = false;
  @Input() title = 'common.actions.confirm_action';
  @Input() message = 'common.actions.confirm_proceed';
  @Input() testId?: string;
  @Input() buttons: ConfirmationButton[] = [
    { label: 'common.actions.cancel', action: 'cancel', outlined: true },
    { label: 'common.actions.confirm', action: 'confirm', severity: 'info' },
  ];
  @Input() width: string = '500px';
  @Input() hasCustomContent: boolean = false;

  @Output() emitOnClick = new EventEmitter<string>();
  @Output() visibleChange = new EventEmitter<boolean>();

  get dialogStyle(): { width: string; maxWidth?: string } {
    const style: { width: string; maxWidth?: string } = { width: this.width };
    if (this.width.includes('px') && Number.parseFloat(this.width) > 800) {
      style.maxWidth = '95vw';
    }
    return style;
  }

  get canClose(): boolean {
    // Prevent closing if any button is loading
    return !this.buttons.some((btn) => btn.loading === true);
  }

  getButtonType(button: ConfirmationButton): ConfirmationButton['severity'] {
    return button.severity || 'secondary';
  }

  onClick(action: string): void {
    // Find the button that was clicked
    const clickedButton = this.buttons.find((btn) => btn.action === action);

    // Don't emit if button is disabled or loading
    if (clickedButton?.disabled || clickedButton?.loading) {
      return;
    }

    this.emitOnClick.emit(action);
    if (action === 'cancel') {
      this.visible = false;
      this.visibleChange.emit(false);
    }
  }

  onDialogHide(): void {
    // Prevent closing if any button is loading
    const hasLoadingButton = this.buttons.some((btn) => btn.loading === true);
    if (hasLoadingButton) {
      return;
    }

    // When dialog is closed via X button or escape key, treat it as cancel
    this.emitOnClick.emit('cancel');
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
